import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CookieStorage,
} from 'amazon-cognito-identity-js'
import { Config, CognitoIdentityCredentials } from 'aws-sdk'

export default class Cognito {
  
  configure (config) {
    if (config.userPool) {
      this.userPool = config.UserPoolId
    } else {
      this.userPool = new CognitoUserPool({
        UserPoolId: config.UserPoolId,
        ClientId: config.ClientId,
        Storage: new CookieStorage({domain: config.ParentDomain, secure: config.Secure, expires: config.Expires})
      })
    }
    Config.region = config.Region
    Config.credentials = new CognitoIdentityCredentials({
      IdentityPoolId: config.IdentityPoolId
    })
    this.options = config
  }

  static install = (Vue, options) => {
    Object.defineProperty(Vue.prototype, '$cognito', {
      get () { return this.$root._cognito }
    })

    Vue.mixin({
      beforeCreate () {
        if (this.$options.cognito) {
          this._cognito = this.$options.cognito
          this._cognito.configure(options)
        }
      }
    })
  }

  /**
   * signup by username, password
   * username = emailとしてUserAttributeにも登録
   */
  signUp (username, password, familyName, givenName, companyName, tel, zipcode, address) {
    const dataEmail = { Name: 'email', Value: username }
    const dataFname = { Name: 'family_name', Value: familyName }
    const dataGname = { Name: 'given_name', Value: givenName }
    const dataCname = { Name: 'custom:company_name', Value: companyName}
    const dataTel = { Name: 'custom:tel', Value: tel}
    const dataZip = { Name: 'custom:zipcode', Value: zipcode}
    const dataAddress = { Name: 'address', Value: address}
    const attributeList = []
    attributeList.push(new CognitoUserAttribute(dataEmail))
    attributeList.push(new CognitoUserAttribute(dataFname))
    attributeList.push(new CognitoUserAttribute(dataGname))
    attributeList.push(new CognitoUserAttribute(dataAddress))
    attributeList.push(new CognitoUserAttribute(dataCname))
    attributeList.push(new CognitoUserAttribute(dataTel))
    attributeList.push(new CognitoUserAttribute(dataZip))
    return new Promise((resolve, reject) => {
      this.userPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err)
        } else {
          resolve(result)
        }
      })
    })
  }

  /**
   * user confirmation by username, confirmation code
   */
  confirmation (username, confirmationCode) {
    const userData = {
      Username: username,
      Pool: this.userPool, 
      Storage: new CookieStorage({
        domain: this.options.ParentDomain,
        secure: this.options.Secure,
        expires: this.options.Expires
      })
    }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) {
          reject(err)
        } else {
          resolve(result)
        }
      })
    })
  }

  /**
   * update attributes
   */
  updateAttributes (email, familyName, givenName, companyName, tel, zipcode, address) {
    const cognitoUser = this.userPool.getCurrentUser()
    const dataEmail = { Name: 'email', Value: email }
    const dataFname = { Name: 'family_name', Value: familyName }
    const dataGname = { Name: 'given_name', Value: givenName }
    const dataCname = { Name: 'custom:company_name', Value: companyName}
    const dataTel = { Name: 'custom:tel', Value: tel}
    const dataZip = { Name: 'custom:zipcode', Value: zipcode}
    const dataAddress = { Name: 'address', Value: address}
    const attributeList = []
    attributeList.push(new CognitoUserAttribute(dataEmail))
    attributeList.push(new CognitoUserAttribute(dataFname))
    attributeList.push(new CognitoUserAttribute(dataGname))
    attributeList.push(new CognitoUserAttribute(dataCname))
    attributeList.push(new CognitoUserAttribute(dataTel))
    attributeList.push(new CognitoUserAttribute(dataZip))
    attributeList.push(new CognitoUserAttribute(dataAddress))
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        } else {
          if (!session.isValid()) {
            reject(session)
          } else {
            cognitoUser.updateAttributes(attributeList, (err, result) => {
              if (err) {
                reject(err)
              } else {
                resolve(result)
              }
            })
          }
        }
      })
    })
  }

  /**
   * get attributes
   * names: array of keys
   */
  getUserAttributes (names) {
    const cognitoUser = this.userPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        } else {
          if (!session.isValid()) {
            reject(session)
          } else {
            cognitoUser.getUserAttributes((err, result) => {
              const attributes = []
              for (var i = 0; i < result.length; i++) {
                if (names.includes(result[i].getName())) attributes[result[i].getName()] = result[i].getValue()
              }
              resolve(attributes)
            })
          }
        }
      })
    })
  }

  /**
   * change password
   */
  changePassword (oldPassword, newPassword) {
    const cognitoUser = this.userPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        } else {
          if (!session.isValid()) {
            reject(session)
          } else {
            cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
              if (err) {
                reject(err)
              } else {
                resolve(result)
              }
            })
          }
        }
      })
    })
  }

  /**
   * login by username, password
   */
  login (username, password) {
    const userData = {
      Username: username,
      Pool: this.userPool, 
      Storage: new CookieStorage({
        domain: this.options.ParentDomain,
        secure: this.options.Secure,
        expires: this.options.Expires
      })
    }
    const cognitoUser = new CognitoUser(userData)
    const authenticationData = { Username: username, Password: password }
    const authenticationDetails = new AuthenticationDetails(authenticationData)
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          resolve(result)
        },
        onFailure: (err) => {
          reject(err)
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          cognitoUser.completeNewPasswordChallenge(password, userAttributes, {
            onSuccess: (result) => {
              resolve(result)
            },
            onFailure: (err) => {
              reject(err)
            }
          })
        }
      })
    })
  }

  /**
   * ログインしているかの判定
   */
  isAuthenticated () {
    const cognitoUser = this.userPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      if (cognitoUser === null) { reject(cognitoUser) }
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        } else {
          if (!session.isValid()) {
            reject(session)
          } else {
            resolve(session)
          }
        }
      })
    })
  }

  /**
   * forgot password
   */
  forgotPassword (username) {
    const userData = {
      Username: username,
      Pool: this.userPool, 
      Storage: new CookieStorage({
        domain: this.options.ParentDomain,
        secure: this.options.Secure,
        expires: this.options.Expires
      })
    }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve(result)
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  /**
   * confirm passowrd
   */
  confirmPassword (username, verificationCode, newPassword) {
    const userData = {
      Username: username,
      Pool: this.userPool, 
      Storage: new CookieStorage({
        domain: this.options.ParentDomain,
        secure: this.options.Secure,
        expires: this.options.Expires
      })
    }
    const cognitoUser = new CognitoUser(userData)
    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: (result) => {
          resolve(result)
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  /**
   * ログアウト
   */
  logout () {
    this.userPool.getCurrentUser().signOut()
  }

  /**
   * 退会
   */
  deleteUser () {
    const cognitoUser = this.userPool.getCurrentUser()
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err)
        } else {
          if (!session.isValid()) {
            reject(session)
          } else {
            cognitoUser.deleteUser((err, result) => {
              if (err) {
                return reject(err);
              }
              resolve(result);
            })
          }
        }
      })
    })
  }
}

if (!Array.prototype.includes) {
  Object.defineProperty(Array.prototype, 'includes', {
    value: function(searchElement, fromIndex) {
      if (this == null) {
        throw new TypeError('"this" is null or not defined');
      }
      var o = Object(this);
      var len = o.length >>> 0;
      if (len === 0) {
        return false;
      }
      var n = fromIndex | 0;
      var k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);
      while (k < len) {
        if (o[k] === searchElement) {
          return true;
        }
        k++;
      }
      return false;
    }
  });
}



// WEBPACK FOOTER //
// ./src/cognito/cognito.js